import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import Actions from "../../../../Actions";
import auth from "../../../../utils/AuthController";
import ModalYesNo from "../../../Modal__YesNo.jsx";
import { CampaignCreationStore, withRefluxStore } from "../../../../Stores";
import CreateInvoiceAddressModal from "../../campaign-creation/invoice-address-modal/CreateInvoiceAddressModal.jsx";
import TopUpCredits from "./topup-button/TopUpCredits";

import {
  extractCreativePictures,
  getDiscountFromSource,
  getPrepaidCredit,
  getSubtotal,
  getTaxes,
  getTotal,
  getDiscountItems,
  getCombinedBudget,
  assembleCampaignData
} from "./utils/utils.js";
import {
  CampaignSection,
  InvoiceSection,
  AnalysisSection,
  SumSection,
  VoiceSection,
  AssetsSection
} from "./sections";
import {
  DetailsReview,
  DetailsSticky,
  HorizontalLine,
  OverviewCards,
  PageHeader,
  ReviewContainer
} from "./BookingOverview.styled.js";
import logger from "../../../../utils/LoggerFrontend.js";
import { Store__System } from "@talentbait/shared-components";

const BookingOverview = ({
  activeDate,
  bookingStyle,
  campaignDuration,
  campaignGoal,
  campaignManager,
  canUseWebsitePictures,
  companyName,
  companyId,
  jobId,
  currentlySelectedAddress,
  draftCampaignId,
  extraBudgetLocations = [],
  invoiceAddresses,
  isBooking,
  isRemoteJob = false,
  jobCategory,
  jobCategoryId,
  jobPostingUrl,
  jobTitle,
  lifeTimeBudget,
  location,
  minClicks,
  orgaId,
  organization,
  packageId,
  paymentStatus,
  paymentType = "invoice",
  pictures,
  poNumber,
  posts,
  predictions,
  selectedChannelList,
  selectedChannels,
  recommendedChannels,
  selfServiceBookingStyle,
  startDate,
  willUploadPicturesLater,
  optimizeAnalysis,
  feedbackAnalysis,
  spotifyHumanVoice,
  // Store
  emitCampaignCreation: isEmitCampaignCreation,
  isLoadingInvoiceAddresses,
  userProfile,
  locale
}) => {
  const { t } = useTranslation();
  useEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

  // Local state for maintaining which email address is used for the invoice
  const [selectedInvoiceEmail, setSelectedInvoiceEmail] = useState({});

  // Local state for maintaining which address is used for the invoice
  const [selectedInvoiceAddressData, setSelectedInvoiceAddressData] = useState({});

  const [coupon, setCoupon] = useState();
  const [vatNumber, setVatNumber] = useState("");
  const [showStreamingWarning, setShowStreamingWarning] = useState();
  const [isInitialModal, setIsInitialModal] = useState(true);

  const hasSpotify = selectedChannels.some(
    ({ channel_short_name }) => channel_short_name === "spotify"
  );

  const [addOns, setAddOns] = useState({
    optimizeAnalysis: !organization?.isAgency ? optimizeAnalysis : null,
    feedbackAnalysis: !organization?.isAgency ? feedbackAnalysis : null,
    spotifyHumanVoice: hasSpotify ? spotifyHumanVoice : null
  });

  useEffect(() => {
    setAddOns((prev) => ({ ...prev, spotifyHumanVoice: hasSpotify ? spotifyHumanVoice : null }));
  }, [hasSpotify, spotifyHumanVoice]);

  useEffect(() => {
    if (!isLoadingInvoiceAddresses && !invoiceAddresses?.length && paymentType !== "prepaid") {
      openAddressModal();
    } else {
      if (isInitialModal) closeAddressModal();
      setIsInitialModal(false);
    }
  }, [
    isLoadingInvoiceAddresses,
    invoiceAddresses,
    paymentType,
    openAddressModal,
    closeAddressModal,
    isInitialModal
  ]);

  const allCreativeImages = extractCreativePictures(posts);

  const { campaignBudget, shutterstockImagesPrice, combinedBudget } = getCombinedBudget({
    allCreativeImages,
    extraBudgetLocations,
    lifeTimeBudget,
    addOns
  });

  const streamingChannels = useMemo(
    () =>
      Object.entries(posts || {}).reduce((totalChannels, [channel, channelPosts = []]) => {
        // Check if channelPosts is an array before using .some()
        if (Array.isArray(channelPosts)) {
          if (channelPosts.some(({ isAutoGenerationStreaming }) => isAutoGenerationStreaming)) {
            totalChannels.add(channel);
          }
        } else {
          // If it's an object with isAutoGenerationStreaming property, check it directly
          if (channelPosts && channelPosts.isAutoGenerationStreaming) {
            totalChannels.add(channel);
          }
        }
        return totalChannels;
      }, new Set()),
    [posts]
  );

  useEffect(() => {
    if (!currentlySelectedAddress && invoiceAddresses?.length) {
      Actions.campaignCreateUpdateReviewData({ currentlySelectedAddress: invoiceAddresses.at(-1) });
    }
  }, [invoiceAddresses, currentlySelectedAddress]);

  useEffect(() => {
    const loadNrCampaignsFailed = Actions.getAllUserCampaignsById.failed.listen((err) => {
      logger.debug("error:BookingFinished:getAllUserCampaignsById:failed", "called...", { err });
    });

    const loadNrCampaignsHandler = Actions.getAllUserCampaignsById.completed.listen(
      ({ payload }) => {
        logger.debug("BookingFinished:getAllUserCampaignsById:completed", "called...", payload);

        Actions.campaignUpdateIsFirstCampaign({
          isFirstCampaign:
            payload?.campaigns.length === 0 ||
            payload?.campaigns.every(({ status }) => status === "DRAFT")
        });
      }
    );

    Actions.getAllUserCampaignsById(userProfile.userId);

    return () => {
      loadNrCampaignsFailed();
      loadNrCampaignsHandler();
    };
  }, [userProfile.userId]);

  useEffect(() => {
    if (isEmitCampaignCreation) {
      bookCampaign();
      Actions.emitCampaignCreation(false);
    }
  }, [isEmitCampaignCreation]);

  useEffect(() => {
    const { emails, addresses } = currentlySelectedAddress || {};

    if (emails?.length) {
      setSelectedInvoiceEmail(emails.at(-1) || "");
    } else {
      setSelectedInvoiceEmail("");
    }

    if (addresses?.length) {
      setSelectedInvoiceAddressData(addresses.at(-1) || {});
    } else {
      setSelectedInvoiceAddressData({});
    }
  }, [currentlySelectedAddress]);

  useEffect(() => {
    Actions.updateDebitoorCompanyAddress.completed.listen(({ payload: returnedAddress } = {}) => {
      logger.log("BookingOverview:updateDebitoorCompanyAddress", "called...", returnedAddress);

      setSelectedInvoiceAddressData({
        ...selectedInvoiceAddressData,
        street: returnedAddress?.street,
        countryCode: returnedAddress?.countryCode,
        city: returnedAddress?.city,
        zip: returnedAddress?.zip
      });

      setSelectedInvoiceEmail(returnedAddress?.email);
    });
  }, [selectedInvoiceAddressData]);

  useEffect(() => {
    const refInvoiceAddresses = Actions.getCustomerAddresses.completed.listen((result) => {
      logger.log("BookingOverview:getCustomerAddresses", "fetched customer address", { result });
      if (result.length === 0) {
        Actions.campaignCreateUpdateReviewData({ currentlySelectedAddress: {} });
      }
    });
    const refFailedInvoiceAddresses = Actions.getCustomerAddresses.failed.listen((error) => {
      logger.log("error:BookingOverview:getCustomerAddresses", "Unable to fetch customer address", {
        error
      });
    });
    Actions.getCustomerAddresses(organization?.invoicesReceivers, false);

    return () => {
      logger.debug("BookingOverview:unmounted", "called");
      refInvoiceAddresses();
      refFailedInvoiceAddresses();
    };
  }, [organization]);

  useEffect(() => {
    const invoiceCreated = Actions.createDebitoorCompany.completed.listen(() => {
      Actions.toggleModal("showGenericModal", false);
    });

    return () => invoiceCreated();
  }, []);

  const bookCampaign = ({ ignoreStreamingWarning = false } = {}) => {
    if (isBooking) {
      return Actions.addNotification(t("Campaign is being created"));
    }

    if (streamingChannels.size && !ignoreStreamingWarning) {
      return setShowStreamingWarning(true);
    }

    const { withAdPreview, campaignData } = assembleCampaignData({
      user: auth.getCurrentUser(),
      predictions,
      campaignDuration,
      recommendedChannels,
      lifeTimeBudget,
      allCreativeImages,
      addOns,
      activeDate,
      bookingStyle,
      campaignGoal,
      optimizeAnalysis,
      feedbackAnalysis,
      spotifyHumanVoice: hasSpotify ? spotifyHumanVoice : null,
      campaignName: jobTitle.trim(),
      canUseWebsitePictures: canUseWebsitePictures,
      companyName: companyName.trim(),
      companyId,
      createdDate: new Date().toISOString(),
      deployed: true,
      extraBudgetLocations: extraBudgetLocations || [],
      hasNewPreviewFlow: true,
      invoicing: {
        organizationId: currentlySelectedAddress?.id || "",
        entity: currentlySelectedAddress?.name || "",
        email: selectedInvoiceEmail?.value || "",
        street: selectedInvoiceAddressData?.street || "",
        city: selectedInvoiceAddressData?.city || "",
        zip: selectedInvoiceAddressData?.zip || "",
        countryName: selectedInvoiceAddressData?.countryName || "",
        countryCode: selectedInvoiceAddressData?.countryCode || "",
        vatNumber
      },
      isRemoteJob,
      minClicks,
      startDate,
      locale,
      jobId,
      selectedChannels: selectedChannelList,
      campaignManager: campaignManager?.id,
      isCreatedFromDraft: !!draftCampaignId,
      channels: selectedChannelList.map(({ channel_short_name }) => channel_short_name),
      jobCategory: jobCategory,
      jobCategoryId: jobCategoryId || "NONE",
      jobPostingUrl: jobPostingUrl.trim(),
      jobTitle: jobTitle.trim(),
      combinedBudget: Number(combinedBudget),
      campaignBudget: Number(campaignBudget),
      locations: location,
      orgaName: organization?.name || "not set",
      organizationId: orgaId,
      packageId: packageId,
      paymentStatus: paymentStatus || "",
      pictures: pictures,
      source: "WEB_INTERFACE",
      status: startDate ? "SCHEDULED" : "IN_PROGRESS",
      willUploadPicturesLater: willUploadPicturesLater,
      couponId: coupon?.id,
      poNumber: poNumber || "",
      selfServiceBookingStyle
    });

    const previewData = {};
    Actions.createCampaignAndSave(orgaId, campaignData, previewData, withAdPreview);
  };

  const openAddressModal = useCallback(() => {
    Actions.toggleModal("showGenericModal", true, {
      headline: t("Insert new billing address"),
      widthVersion: "small",
      view: (
        <div style={{ padding: 15 }}>
          <CreateInvoiceAddressModal
            onCancel={closeAddressModal}
            organizationId={orgaId}
            companyName={currentlySelectedAddress?.name}
          />
        </div>
      )
    });
  }, [t, closeAddressModal, orgaId, currentlySelectedAddress?.name]);

  const closeAddressModal = useCallback(() => {
    Actions.toggleModal("showGenericModal", false);
  }, []);

  const editInvoiceAddress = useCallback(
    (contactToEdit, addressToEdit, emailToEdit) => {
      if (contactToEdit && addressToEdit && emailToEdit) {
        Actions.showEditInvoiceAddressModal(contactToEdit, addressToEdit, emailToEdit);
      } else {
        openAddressModal();
      }
    },
    [openAddressModal]
  );

  const onAcceptStreamingWarning = () => {
    setShowStreamingWarning(false);
  };

  const onIgnoreStreamingWarning = () => {
    setShowStreamingWarning(false);
    bookCampaign({ ignoreStreamingWarning: true });
  };

  const discountItems = getDiscountItems({ campaignBudget, shutterstockImagesPrice, addOns });

  const discountData = getDiscountFromSource({
    organization,
    coupon,
    items: discountItems,
    isAgency: organization?.isAgency
  });

  const subtotal = getSubtotal({ amount: combinedBudget, discount: discountData?.deduction });
  const credit = getPrepaidCredit({ prepaidBalance: organization?.prepaidStateBalance, subtotal });

  const taxes = getTaxes({
    subtotal,
    credit,
    countryCode: selectedInvoiceAddressData?.countryCode
  });

  const total = getTotal({ subtotal, credit, taxes });

  logger.log("BookingOverview", "Rendered Booking Overview", {
    allCreativeImages,
    posts,
    shutterstockImagesPrice,
    orgaId,
    draftCampaignId,
    jobTitle,
    companyName
  });

  if (!orgaId || !companyName || !jobTitle) {
    return <Redirect to="/" />;
  }

  return (
    <>
      <ReviewContainer>
        <OverviewCards>
          <PageHeader>{t("Booking overview")}</PageHeader>

          <CampaignSection
            jobTitle={jobTitle}
            companyName={companyName}
            startDate={startDate}
            location={location}
            campaignDuration={campaignDuration}
            selectedChannels={selectedChannels}
            campaignBudget={campaignBudget}
            language={userProfile?.language}
          />

          {allCreativeImages.length > 0 ? (
            <AssetsSection allCreativeAssets={allCreativeImages} type="images" />
          ) : null}

          {!organization?.isAgency ? (
            <AnalysisSection
              optimizeAnalysisStore={optimizeAnalysis}
              feedbackAnalysisStore={feedbackAnalysis}
              addOns={addOns}
              setAddOns={setAddOns}
            />
          ) : null}

          {hasSpotify && (
            <VoiceSection
              spotifyHumanVoiceStore={spotifyHumanVoice}
              addOns={addOns}
              setAddOns={setAddOns}
            />
          )}
        </OverviewCards>

        <DetailsReview>
          <DetailsSticky>
            <InvoiceSection
              editInvoiceAddress={editInvoiceAddress}
              currentlySelectedAddress={currentlySelectedAddress}
              selectedInvoiceAddressData={selectedInvoiceAddressData}
              openAddressModal={openAddressModal}
              invoiceEntity={currentlySelectedAddress?.name || organization.name}
              selectedInvoiceEmail={selectedInvoiceEmail}
              isLoadingInvoiceAddresses={isLoadingInvoiceAddresses}
              poNumber={poNumber}
            />

            <HorizontalLine margin="32px 0 16px" />

            <SumSection
              credit={credit}
              imagePrices={shutterstockImagesPrice}
              campaignBudget={campaignBudget}
              taxes={taxes}
              subtotal={subtotal - credit}
              countryCode={selectedInvoiceAddressData?.countryCode}
              discountData={discountData}
              isAgency={organization?.isAgency}
              total={total}
              setVatNumber={setVatNumber}
              organization={organization}
              combinedBudget={combinedBudget}
              selectedChannels={selectedChannels}
              setCoupon={setCoupon}
              addOns={addOns}
              language={userProfile?.language}
            />
            {total > 0 && !organization?.isAgency && <TopUpCredits />}
          </DetailsSticky>
        </DetailsReview>
      </ReviewContainer>

      <ModalYesNo
        yesClicks={onIgnoreStreamingWarning}
        noClick={onAcceptStreamingWarning}
        show={showStreamingWarning}
        headline={t("Some of your channels are still streaming")}
        text={
          <Trans>
            {{ channelsNum: streamingChannels.size }} channels are still streaming AI generated text
            and will fall back to none AI text.
            <br />
            Do you want to continue?
          </Trans>
        }
      />
    </>
  );
};

export default withRefluxStore(
  BookingOverview,
  [CampaignCreationStore, Store__System],
  ["emitCampaignCreation", "isLoadingInvoiceAddresses", "userProfile", "locale"]
);
